import { SyncTriggerButtons } from "@/components/sync/sync-trigger-buttons";
import { AppLink } from "@/components/ui/app-link";
import { Badge } from "@/components/ui/badge";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LocalDate } from "@/components/ui/local-date";
import { PAGE_SIZE_OPTIONS, Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { useUrlTableSort } from "@/components/ui/sortable-table";
import { ApiError, api } from "@/lib/api";
import { paginationSearchFields, sortSearchFields } from "@/lib/search";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/sync` — admin-only sync queue overview. Lists recent SyncJob
 * rows with a status badge, a "completed · N warnings" badge when
 * a successful run reported warnings, and links into the per-job
 * detail page (still Next-owned).
 *
 * Header carries the SyncTriggerButtons panel (manual enqueue of
 * the GitLab / Fakturoid sync jobs).
 */

const SORT_KEYS = ["type", "status", "started", "finished"] as const;
type SortKey = (typeof SORT_KEYS)[number];

const syncSearchSchema = z.object({
  q: z.string().optional(),
  ...sortSearchFields(SORT_KEYS),
  ...paginationSearchFields,
});

type SyncSearch = z.infer<typeof syncSearchSchema>;

type SyncJob = {
  id: string;
  type: string;
  status: "pending" | "running" | "completed" | "failed";
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  result: unknown;
  /** Job input — a manual Clockify backfill carries `{ since }`;
   *  periodic syncs run on the default window and have none. */
  payload: { since?: string } | null;
};

/** The `since` of a manually-triggered backfill, or null for a
 *  periodic run (which uses the default 30-day window). */
function backfillSince(job: SyncJob): string | null {
  const s = job.payload?.since;
  return typeof s === "string" ? s.slice(0, 10) : null;
}

type SyncResponse = {
  jobs: SyncJob[];
  total: number;
  page: number;
  pageSize: number;
};

const statusVariant: Record<
  string,
  "default" | "secondary" | "success" | "destructive" | "warning" | "outline"
> = {
  pending: "secondary",
  running: "default",
  completed: "success",
  failed: "destructive",
  warning: "warning",
};

function warningCount(result: unknown): number {
  if (result && typeof result === "object" && "warnings" in result) {
    const w = (result as { warnings?: unknown }).warnings;
    if (Array.isArray(w)) return w.length;
  }
  return 0;
}

export const Route = createFileRoute("/_app/sync/")({
  validateSearch: syncSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
    sort: search.sort,
    dir: search.dir,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: async ({ deps }) => {
    const params = new URLSearchParams();
    if (deps.q) params.set("q", deps.q);
    if (deps.sort) params.set("sort", deps.sort);
    if (deps.dir) params.set("dir", deps.dir);
    if (deps.page) params.set("page", String(deps.page));
    if (deps.pageSize) params.set("pageSize", String(deps.pageSize));
    const qs = params.toString();
    try {
      return await api.get<SyncResponse>(qs ? `/api/sync?${qs}` : "/api/sync");
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        throw redirect({ to: "/my" });
      }
      throw err;
    }
  },
  component: SyncPage,
});

function SyncPage() {
  const { t } = useTranslation();
  const { jobs, total, page, pageSize } = Route.useLoaderData();
  const search: SyncSearch = Route.useSearch();
  const navigate = useNavigate();

  const sort = useUrlTableSort({
    sortKey: search.sort ?? null,
    sortDir: search.dir ?? "desc",
    onChange: (key, dir) =>
      navigate({
        to: "/sync",
        search: { ...search, sort: key as SortKey, dir, page: undefined },
      }),
  });

  const columns: Array<DataTableColumn<SyncJob>> = [
    {
      key: "type",
      header: t("sync.columns.type"),
      sortAccessor: (job) => job.type,
      cell: (job) => {
        const since = backfillSince(job);
        return (
          <>
            <span className="font-mono text-sm">{job.type}</span>
            {since && (
              <Badge variant="outline" className="ml-2 text-[10px]">
                {t("sync.backfillSince", { since })}
              </Badge>
            )}
          </>
        );
      },
    },
    {
      key: "status",
      header: t("sync.columns.status"),
      sortAccessor: (job) => job.status,
      cell: (job) => {
        const wc = warningCount(job.result);
        const effectiveStatus = job.status === "completed" && wc > 0 ? "warning" : job.status;
        return (
          <Badge variant={statusVariant[effectiveStatus] ?? "outline"}>
            {effectiveStatus === "warning"
              ? t("sync.completedWarnings", { count: wc })
              : t(`sync.status.${effectiveStatus}`, { defaultValue: effectiveStatus })}
          </Badge>
        );
      },
    },
    {
      key: "started",
      header: t("sync.columns.started"),
      sortAccessor: (job) => job.startedAt,
      cellClassName: "text-sm text-muted-foreground",
      cell: (job) => (job.startedAt ? <LocalDate value={job.startedAt} withTime /> : "—"),
    },
    {
      key: "finished",
      header: t("sync.columns.finished"),
      sortAccessor: (job) => job.finishedAt,
      cellClassName: "text-sm text-muted-foreground",
      cell: (job) => (job.finishedAt ? <LocalDate value={job.finishedAt} withTime /> : "—"),
    },
    {
      // `Info` is a derived display field (joined from `error` and
      // `result.warnings`) and can't be ordered in SQL — it stays
      // unsortable now that sort is server-driven.
      key: "info",
      header: t("sync.columns.info"),
      cellClassName: "text-sm max-w-xs truncate",
      cell: (job) => {
        const wc = warningCount(job.result);
        return job.error ? (
          <span className="text-red-600">{job.error}</span>
        ) : wc > 0 ? (
          <span className="text-amber-600">{t("sync.projectsSkipped", { count: wc })}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      key: "detail",
      header: null,
      headClassName: "w-16",
      cell: (job) => (
        <AppLink href={`/sync/${job.id}`} className="text-xs text-primary hover:underline">
          {t("sync.detail")}
        </AppLink>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t("sync.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t("sync.jobsTotal", { count: total })}
          </p>
        </div>
        <SyncTriggerButtons />
      </div>

      <SearchInput placeholder={t("sync.filterPlaceholder")} />

      <DataTable
        columns={columns}
        rows={jobs}
        rowKey={(job) => job.id}
        sort={sort}
        emptyState={t("sync.empty")}
      />

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZE_OPTIONS}
        storageKey="sync"
        basePath="/sync"
        searchParams={{
          q: search.q,
          sort: search.sort,
          dir: search.dir,
          pageSize: search.pageSize,
        }}
      />
    </div>
  );
}
